import React, { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '../components/Layout';
import api from '../services/api';
import { toast } from 'react-toastify';

const ERP_TYPES = [
  { value: 'PHC', label: 'PHC Advanced' },
  { value: 'Sage50', label: 'Sage 50C' },
  { value: 'Sage100', label: 'Sage 100C' },
  { value: 'XD', label: 'XD Software' },
  { value: 'Wintouch', label: 'Wintouch' },
  { value: 'Primavera', label: 'Primavera' },
];

const MODULES = [
  { key: 'Artigos', label: 'Artigos', icon: '📦', subtitle: 'Produtos e serviços' },
  { key: 'Clientes', label: 'Clientes', icon: '👥', subtitle: 'Base de clientes' },
  { key: 'Stock', label: 'Stock', icon: '📊', subtitle: 'Quantidades e movimentos' },
  { key: 'Armazens', label: 'Armazéns', icon: '🏭', subtitle: 'Locais de inventário' },
  { key: 'Familias', label: 'Famílias', icon: '🧩', subtitle: 'Categorias de artigos' },
];

const STATUS_COLORS = {
  Success: '#22c55e',
  Error: '#ef4444',
  Running: '#f59e0b',
};

export default function AdminErpPage() {
  const [config, setConfig] = useState({
    isEnabled: false,
    baseUrl: 'http://localhost:5300',
    apiKey: '',
    erpType: 'PHC',
    activeModules: [],
    autoSyncEnabled: false,
    autoSyncIntervalMinutes: 60,
    pushClientesToErp: false,
    pushDocumentosToErp: false,
    defaultDocumentType: 'FT',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [diagResult, setDiagResult] = useState(null);
  const [syncHistory, setSyncHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('config');

  const loadConfig = useCallback(async () => {
    try {
      const res = await api.get('/erp/config');
      const d = res.data;
      setConfig({
        isEnabled: d.isEnabled ?? false,
        baseUrl: d.baseUrl ?? 'http://localhost:5300',
        apiKey: d.apiKey ?? '',
        erpType: d.erpType ?? 'PHC',
        activeModules: d.activeModules ?? [],
        autoSyncEnabled: d.autoSyncEnabled ?? false,
        autoSyncIntervalMinutes: d.autoSyncIntervalMinutes ?? 60,
        pushClientesToErp: d.pushClientesToErp ?? false,
        pushDocumentosToErp: d.pushDocumentosToErp ?? false,
        defaultDocumentType: d.defaultDocumentType ?? 'FT',
      });
    } catch (err) {
      if (err.response?.status !== 404) {
        toast.error('Erro ao carregar configuração ERP');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const res = await api.get('/erp/sync/history?limit=20');
      setSyncHistory(Array.isArray(res.data) ? res.data : []);
    } catch {
      setSyncHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => { loadConfig(); }, [loadConfig]);
  useEffect(() => { if (activeTab === 'history') loadHistory(); }, [activeTab, loadHistory]);

  const handleModuleToggle = (mod) => {
    setConfig(prev => ({
      ...prev,
      activeModules: prev.activeModules.includes(mod)
        ? prev.activeModules.filter(m => m !== mod)
        : [...prev.activeModules, mod],
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/erp/config', config);
      toast.success('Configuração ERP guardada com sucesso');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erro ao guardar configuração');
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setDiagResult(null);
    try {
      const res = await api.get('/erp/diagnostico');
      setDiagResult({ success: true, data: res.data });
    } catch (err) {
      setDiagResult({ success: false, message: err.response?.data?.message || err.message });
    } finally {
      setTesting(false);
    }
  };

  const handleSyncAll = async () => {
    setSyncing(true);
    try {
      await api.post('/erp/sync/all');
      toast.success('Sincronização iniciada com sucesso');
      setTimeout(() => loadHistory(), 2000);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erro ao iniciar sincronização');
    } finally {
      setSyncing(false);
    }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleString('pt-PT') : '—';

  const lastRun = syncHistory[0];
  const successCount = syncHistory.filter(h => h.status === 'Success').length;
  const errorCount = syncHistory.filter(h => h.status === 'Error').length;

  const card = {
    background: 'var(--bg-card)',
    border: '1px solid var(--border-color)',
    borderRadius: 14,
    padding: '1.25rem 1.35rem',
    marginBottom: '1rem',
    boxShadow: '0 1px 3px rgba(15, 23, 42, 0.04)',
  };

  const tabBtn = (active) => ({
    padding: '0.55rem 1.1rem',
    borderRadius: 10,
    border: active ? '1px solid var(--accent-primary)' : '1px solid transparent',
    cursor: 'pointer',
    fontWeight: active ? 700 : 500,
    fontSize: '0.88rem',
    background: active ? 'linear-gradient(180deg, #eef2ff 0%, #e0e7ff 100%)' : 'transparent',
    color: active ? '#4338ca' : '#475569',
    transition: 'all 0.15s',
  });

  if (loading) {
    return (
      <DashboardLayout userType="admin">
        <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}>
          <div className="spinner" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout userType="admin">
      <div style={{ padding: '1rem' }}>
        {/* Page header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', gap: '1rem', flexWrap: 'wrap' }}>
          <div>
            <h1 style={{ margin: 0, fontSize: '1.6rem', fontWeight: 800, color: 'var(--info-text)' }}>
              🔗 Integração ERP
            </h1>
            <p style={{ margin: '0.25rem 0 0 0', color: 'var(--info)', fontSize: '0.92rem' }}>
              Ligação ao ERP via ZapsysV2 — sincronização de artigos, clientes, stock e armazéns
            </p>
          </div>
          <span style={{
            padding: '0.4rem 0.9rem',
            borderRadius: 999,
            fontSize: '0.78rem',
            fontWeight: 700,
            background: config.isEnabled ? 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)' : '#f1f5f9',
            color: config.isEnabled ? '#15803d' : '#64748b',
            border: `1px solid ${config.isEnabled ? '#86efac' : '#e2e8f0'}`,
          }}>
            {config.isEnabled ? '● Ativo' : '○ Inativo'}
          </span>
        </div>

        {/* Stat cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '0.75rem', marginBottom: '1.25rem' }}>
          {[
            { l: 'Tipo ERP', v: config.erpType, c: 'var(--info)', i: '🏷️' },
            { l: 'Módulos Ativos', v: config.activeModules.length, c: 'var(--success)', i: '🧩' },
            { l: 'Auto Sync', v: config.autoSyncEnabled ? `${config.autoSyncIntervalMinutes}m` : 'Off', c: 'var(--accent-secondary)', i: '⏱️' },
            { l: 'Última Sync', v: lastRun ? formatDate(lastRun.startedAt).split(',')[0] : '—', c: 'var(--info-text)', i: '📅' },
            { l: 'Com Sucesso', v: successCount, c: 'var(--success-text)', i: '✅' },
            { l: 'Com Erros', v: errorCount, c: 'var(--error)', i: '⚠️' },
          ].map((s, i) => (
            <div key={i} style={{ background: 'var(--bg-card)', borderRadius: 12, padding: '1rem', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <span style={{ fontSize: '1.85rem' }}>{s.i}</span>
              <div>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{s.l}</div>
                <div style={{ fontSize: '1.15rem', fontWeight: 700, color: s.c }}>{s.v}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 8, marginBottom: '1rem', flexWrap: 'wrap' }}>
          {[
            { key: 'config', label: 'Configuração' },
            { key: 'modules', label: 'Módulos & Sync' },
            { key: 'history', label: 'Histórico' },
          ].map(tab => (
            <button key={tab.key} style={tabBtn(activeTab === tab.key)} onClick={() => setActiveTab(tab.key)}>
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === 'config' && (
          <>
            <div style={card}>
              <h3 style={{ margin: '0 0 1rem', fontSize: '1rem', fontWeight: 700, color: 'var(--info-text)' }}>Ligação ZapsysV2</h3>

              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: '1rem', padding: '0.75rem 1rem', background: 'var(--info-bg)', borderRadius: 10, border: '1px solid var(--info)' }}>
                <input
                  type="checkbox"
                  id="isEnabled"
                  checked={config.isEnabled}
                  onChange={e => setConfig(p => ({ ...p, isEnabled: e.target.checked }))}
                  style={{ width: 18, height: 18, cursor: 'pointer' }}
                />
                <label htmlFor="isEnabled" style={{ fontWeight: 600, cursor: 'pointer', color: 'var(--info-text)' }}>
                  Ativar integração ERP via ZapsysV2
                </label>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '0.85rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: 4, fontSize: '0.82rem', fontWeight: 600, color: 'var(--info-text)' }}>Tipo de ERP</label>
                  <select className="form-input" value={config.erpType} onChange={e => setConfig(p => ({ ...p, erpType: e.target.value }))} disabled={!config.isEnabled} style={{ width: '100%' }}>
                    {ERP_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 4, fontSize: '0.82rem', fontWeight: 600, color: 'var(--info-text)' }}>Tipo Documento Padrão</label>
                  <input type="text" className="form-input" value={config.defaultDocumentType} onChange={e => setConfig(p => ({ ...p, defaultDocumentType: e.target.value }))} disabled={!config.isEnabled} placeholder="FT" style={{ width: '100%' }} />
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ display: 'block', marginBottom: 4, fontSize: '0.82rem', fontWeight: 600, color: 'var(--info-text)' }}>URL do ZapsysV2</label>
                  <input type="text" className="form-input" value={config.baseUrl} onChange={e => setConfig(p => ({ ...p, baseUrl: e.target.value }))} disabled={!config.isEnabled} placeholder="http://localhost:5300" style={{ width: '100%' }} />
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ display: 'block', marginBottom: 4, fontSize: '0.82rem', fontWeight: 600, color: 'var(--info-text)' }}>Chave API (X-API-Key)</label>
                  <input type="password" className="form-input" value={config.apiKey} onChange={e => setConfig(p => ({ ...p, apiKey: e.target.value }))} disabled={!config.isEnabled} placeholder="Chave API do ZapsysV2" style={{ width: '100%' }} />
                </div>
              </div>

              <div style={{ marginTop: '1rem', display: 'flex', gap: 8 }}>
                <button className="btn btn-outline" onClick={handleTestConnection} disabled={testing || !config.isEnabled}>
                  {testing ? 'A testar...' : '🔍 Testar Ligação'}
                </button>
              </div>

              {diagResult && (
                <div style={{
                  marginTop: '1rem',
                  padding: '0.85rem 1rem',
                  borderRadius: 10,
                  background: diagResult.success ? '#dcfce7' : '#fee2e2',
                  color: diagResult.success ? '#15803d' : '#dc2626',
                  border: `1px solid ${diagResult.success ? '#86efac' : '#fecaca'}`,
                }}>
                  {diagResult.success ? (
                    <>
                      <strong>✓ Ligação bem-sucedida!</strong>
                      {diagResult.data?.erpType && <span style={{ marginLeft: 8 }}>ERP: {diagResult.data.erpType}</span>}
                      {diagResult.data?.version && <span style={{ marginLeft: 8 }}>Versão: {diagResult.data.version}</span>}
                    </>
                  ) : (
                    <><strong>✗ Falha na ligação:</strong> {diagResult.message}</>
                  )}
                </div>
              )}
            </div>

            <div style={card}>
              <h3 style={{ margin: '0 0 0.5rem', fontSize: '1rem', fontWeight: 700, color: 'var(--info-text)' }}>Integração Bilateral</h3>
              <p style={{ margin: '0 0 1rem', color: 'var(--info)', fontSize: '0.85rem' }}>
                Configurar o envio de dados da plataforma para o ERP
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', padding: '0.6rem 0.75rem', borderRadius: 8, background: 'var(--info-bg)' }}>
                  <input type="checkbox" checked={config.pushClientesToErp} onChange={e => setConfig(p => ({ ...p, pushClientesToErp: e.target.checked }))} disabled={!config.isEnabled} style={{ width: 16, height: 16 }} />
                  <span style={{ fontSize: '0.9rem', color: 'var(--info-text)' }}>👥 Enviar novos clientes para o ERP</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', padding: '0.6rem 0.75rem', borderRadius: 8, background: 'var(--info-bg)' }}>
                  <input type="checkbox" checked={config.pushDocumentosToErp} onChange={e => setConfig(p => ({ ...p, pushDocumentosToErp: e.target.checked }))} disabled={!config.isEnabled} style={{ width: 16, height: 16 }} />
                  <span style={{ fontSize: '0.9rem', color: 'var(--info-text)' }}>📄 Enviar documentos/faturas para o ERP</span>
                </label>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'A guardar...' : '💾 Guardar Configuração'}
              </button>
            </div>
          </>
        )}

        {activeTab === 'modules' && (
          <>
            <div style={card}>
              <h3 style={{ margin: '0 0 0.5rem', fontSize: '1rem', fontWeight: 700, color: 'var(--info-text)' }}>
                Módulos de Sincronização (ERP → Plataforma)
              </h3>
              <p style={{ margin: '0 0 1rem', color: 'var(--info)', fontSize: '0.85rem' }}>
                Selecione os dados a importar do ERP automaticamente
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '0.75rem' }}>
                {MODULES.map(mod => {
                  const active = config.activeModules.includes(mod.key);
                  return (
                    <button
                      key={mod.key}
                      onClick={() => config.isEnabled && handleModuleToggle(mod.key)}
                      disabled={!config.isEnabled}
                      style={{
                        border: `1px solid ${active ? '#93c5fd' : '#e5e7eb'}`,
                        borderRadius: 14,
                        background: active
                          ? 'linear-gradient(180deg, #eff6ff 0%, #dbeafe 100%)'
                          : 'linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)',
                        padding: '0.9rem 0.95rem',
                        cursor: config.isEnabled ? 'pointer' : 'not-allowed',
                        opacity: config.isEnabled ? 1 : 0.55,
                        textAlign: 'left',
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '0.7rem',
                        boxShadow: active ? '0 8px 20px rgba(37, 99, 235, 0.14)' : '0 8px 20px rgba(15, 23, 42, 0.06)',
                        transition: 'all 0.2s ease',
                      }}
                    >
                      <span style={{ fontSize: '1.35rem', lineHeight: 1 }}>{mod.icon}</span>
                      <span style={{ flex: 1 }}>
                        <div style={{ fontWeight: 800, color: 'var(--info-text)', fontSize: '0.9rem' }}>{mod.label}</div>
                        <div style={{ color: 'var(--info)', fontSize: '0.78rem', marginTop: '0.2rem' }}>{mod.subtitle}</div>
                      </span>
                      {active && <span style={{ color: 'var(--info)', fontSize: '1rem', fontWeight: 700 }}>✓</span>}
                    </button>
                  );
                })}
              </div>
            </div>

            <div style={card}>
              <h3 style={{ margin: '0 0 1rem', fontSize: '1rem', fontWeight: 700, color: 'var(--info-text)' }}>Sincronização Automática</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', padding: '0.6rem 0.75rem', borderRadius: 8, background: 'var(--info-bg)' }}>
                  <input type="checkbox" checked={config.autoSyncEnabled} onChange={e => setConfig(p => ({ ...p, autoSyncEnabled: e.target.checked }))} disabled={!config.isEnabled} style={{ width: 16, height: 16 }} />
                  <span style={{ fontSize: '0.9rem', color: 'var(--info-text)' }}>⏱️ Ativar sincronização automática periódica</span>
                </label>
                {config.autoSyncEnabled && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, paddingLeft: 26 }}>
                    <label style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--info-text)' }}>Intervalo (minutos):</label>
                    <input type="number" className="form-input" value={config.autoSyncIntervalMinutes} onChange={e => setConfig(p => ({ ...p, autoSyncIntervalMinutes: parseInt(e.target.value) || 60 }))} min={5} max={1440} style={{ width: 100 }} />
                    <span style={{ fontSize: '0.78rem', color: 'var(--info)' }}>(mínimo 5 min)</span>
                  </div>
                )}
              </div>
            </div>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' }}>
              <button
                onClick={handleSyncAll}
                disabled={syncing || !config.isEnabled}
                style={{
                  border: 'none',
                  background: (syncing || !config.isEnabled) ? '#e5e7eb' : 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                  color: (syncing || !config.isEnabled) ? '#94a3b8' : '#fff',
                  borderRadius: 10,
                  padding: '0.65rem 1.1rem',
                  cursor: (syncing || !config.isEnabled) ? 'not-allowed' : 'pointer',
                  fontWeight: 700,
                  fontSize: '0.88rem',
                  boxShadow: (syncing || !config.isEnabled) ? 'none' : '0 8px 20px rgba(245, 158, 11, 0.25)',
                }}
              >
                {syncing ? 'A sincronizar...' : '🔄 Sincronizar Agora'}
              </button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'A guardar...' : '💾 Guardar'}
              </button>
            </div>
          </>
        )}

        {activeTab === 'history' && (
          <div style={card}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem', flexWrap: 'wrap', gap: 8 }}>
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: 'var(--info-text)' }}>Histórico de Sincronização</h3>
              <button className="btn btn-outline" onClick={loadHistory} disabled={historyLoading} style={{ fontSize: '0.82rem' }}>
                🔄 Atualizar
              </button>
            </div>
            {historyLoading ? (
              <div style={{ textAlign: 'center', padding: '2rem' }}><div className="spinner" /></div>
            ) : syncHistory.length === 0 ? (
              <p style={{ color: 'var(--info)', textAlign: 'center', padding: '2rem', fontSize: '0.9rem' }}>
                Nenhum registo de sincronização
              </p>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid var(--border-color)', background: 'var(--info-bg)' }}>
                      <th style={{ textAlign: 'left', padding: '0.65rem 0.75rem', fontWeight: 700, color: 'var(--info-text)' }}>Módulo</th>
                      <th style={{ textAlign: 'left', padding: '0.65rem 0.75rem', fontWeight: 700, color: 'var(--info-text)' }}>Direção</th>
                      <th style={{ textAlign: 'left', padding: '0.65rem 0.75rem', fontWeight: 700, color: 'var(--info-text)' }}>Estado</th>
                      <th style={{ textAlign: 'right', padding: '0.65rem 0.75rem', fontWeight: 700, color: 'var(--info-text)' }}>Processados</th>
                      <th style={{ textAlign: 'right', padding: '0.65rem 0.75rem', fontWeight: 700, color: 'var(--info-text)' }}>Criados</th>
                      <th style={{ textAlign: 'right', padding: '0.65rem 0.75rem', fontWeight: 700, color: 'var(--info-text)' }}>Atualizados</th>
                      <th style={{ textAlign: 'left', padding: '0.65rem 0.75rem', fontWeight: 700, color: 'var(--info-text)' }}>Data</th>
                    </tr>
                  </thead>
                  <tbody>
                    {syncHistory.map((item, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid var(--info)' }}>
                        <td style={{ padding: '0.6rem 0.75rem', color: 'var(--info-text)', fontWeight: 500 }}>{item.module}</td>
                        <td style={{ padding: '0.6rem 0.75rem' }}>
                          <span style={{ fontSize: '0.75rem', padding: '3px 8px', borderRadius: 6, fontWeight: 600, background: item.direction === 'ERP→Platform' ? '#eff6ff' : '#f0fdf4', color: item.direction === 'ERP→Platform' ? '#1d4ed8' : '#15803d' }}>
                            {item.direction}
                          </span>
                        </td>
                        <td style={{ padding: '0.6rem 0.75rem' }}>
                          <span style={{ color: STATUS_COLORS[item.status] ?? '#6b7280', fontWeight: 600 }}>
                            {item.status}
                          </span>
                          {item.errorMessage && (
                            <span style={{ marginLeft: 6, fontSize: '0.75rem', color: 'var(--error)' }} title={item.errorMessage}>⚠ Erro</span>
                          )}
                        </td>
                        <td style={{ padding: '0.6rem 0.75rem', textAlign: 'right' }}>{item.itemsProcessed ?? 0}</td>
                        <td style={{ padding: '0.6rem 0.75rem', textAlign: 'right' }}>{item.itemsCreated ?? 0}</td>
                        <td style={{ padding: '0.6rem 0.75rem', textAlign: 'right' }}>{item.itemsUpdated ?? 0}</td>
                        <td style={{ padding: '0.6rem 0.75rem', fontSize: '0.78rem', color: 'var(--info)' }}>{formatDate(item.startedAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
